import React, { useEffect, useRef, useState } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Button,
	CircularProgress,
	Icon,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { CheckCircleOutline, RestartAltOutlined, Warning } from '@mui/icons-material';
import { callAPIAsync, callAPISync, getDisabledTabs, getUpdateNotice, updateApiCustomData } from '../../../../utils/api';
import { useLogto } from '@logto/react';
import Logger from '../../../../utils/logger';
import { SnackbarAlert } from '../../../SnackbarAlert';
import { updateUpdateNotice } from '../../../../store/system/systemSlice';
import { useAppDispatch } from '../../../../store/hooks';
import { prometheusTabs } from '../..';
import MaintenanceTabGroup from './MaintenanceTabGroup';

const MaintenanceTab: React.FC = () => {
	const logto = useLogto();
	const tabHolder = useRef<HTMLDivElement | null>(null);
	const dispatch = useAppDispatch();

	const [disabledTabs, setDisabledTabs] = useState<string[]>([]);
	const [selectedTabs, setSelectedTabs] = useState<string[]>([]);

	const [updateNoticeTitle, setUpdateNoticeTitle] = useState('');
	const [updateTitleInput, setUpdateTitleInput] = useState('');
	const [updateNoticeDesc, setUpdateNoticeDesc] = useState<string[]>([]);
	const [updateDescInput, setUpdateDescInput] = useState('');

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState(false);

	const onChangeSelected = (path: string) =>
		selectedTabs.includes(path) ? setSelectedTabs(selectedTabs.filter((tab) => tab !== path)) : setSelectedTabs([...selectedTabs, path]);

	const handleConfirm = async () => {
		setLoading(true);
		const response = await callAPIAsync(logto, (token) =>
			updateApiCustomData(token, {
				disabledTabs: {
					changesMade: !noMaintenanceChangesMade,
					newData: selectedTabs,
				},
				updateNotice: {
					changesMade: !noNoticeChangesMade,
					title: updateTitleInput,
					desc: updateDescInput === '' ? [] : updateDescInput.split(/\r?\n/),
				},
				// changes to faqs not implemented yet
				faqs: {
					changesMade: false,
					newData: [],
				},
			})
		);

		if (response) {
			if (response.ok) {
				const resp = await response.json();
				setDisabledTabs(resp.disabledTabs);
				setSelectedTabs(resp.disabledTabs);
				setUpdateTitleInput(resp.updateNoticeTitle);
				setUpdateNoticeTitle(resp.updateNoticeTitle);
				setUpdateDescInput(resp.updateNoticeDesc.join());
				setUpdateNoticeDesc(resp.updateNoticeDesc);
				dispatch(updateUpdateNotice({ title: resp.updateNoticeTitle, desc: resp.updateNoticeDesc }));
				setSuccess(true);
				setLoading(false);
				setExpandedIdx(null);
			} else {
				setError(`An error occurred when updating custom data (${String(response.statusText)})! `);
				setLoading(false);
				Logger.error(response.statusText);
			}
		} else {
			setError(`Failed to get response from server! `);
			setLoading(false);
			Logger.error('No response from server');
		}
	};

	useEffect(() => {
		callAPISync(logto, (token) => {
			setLoading(true);
			getDisabledTabs(token)
				.then((res) => {
					if (res.ok) {
						if (res.status === 204) {
							return [];
						} else {
							return res.json();
						}
					} else {
						throw new Error(`${res.status} ${res.statusText}`);
					}
				})
				.then((resp: string[]) => {
					setDisabledTabs(resp);
					setSelectedTabs(resp);
					setLoading(false);
				})
				.catch((e) => {
					setError(`An error occurred when fetching maintenenace status (${String(e)})! `);
					setLoading(false);
					Logger.error(e);
				});
			getUpdateNotice(token)
				.then((res) => {
					if (res.ok) {
						if (res.status === 204) {
							return { title: '', desc: [] };
						} else {
							return res.json();
						}
					} else {
						throw new Error(`${res.status} ${res.statusText}`);
					}
				})
				.then((resp: { title: string; desc: string[] }) => {
					setUpdateNoticeTitle(resp.title);
					setUpdateNoticeDesc(resp.desc);
					setUpdateTitleInput(resp.title);
					setUpdateDescInput(resp.desc.join('\n'));
					setLoading(false);
				})
				.catch((e) => {
					setError(`An error occurred when fetching update notice status (${String(e)})! `);
					setLoading(false);
					Logger.error(e);
				});
		});
	}, []);

	const noMaintenanceChangesMade =
		selectedTabs.length === disabledTabs.length && selectedTabs.every((tab, index) => tab === disabledTabs[index]);

	const noNoticeChangesMade = updateTitleInput === updateNoticeTitle && updateDescInput === updateNoticeDesc.join('\n');

	const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

	const onChange = (expanded: boolean, idx: number) => {
		if (expanded) {
			setExpandedIdx(idx);
		} else {
			setExpandedIdx(null);
		}
	};

	const rows = [];
	for (let i = 0; i < prometheusTabs.length; i += 3) {
		rows.push(prometheusTabs.slice(i, i + 3));
	}

	return (
		<>
			<Accordion
				expanded={expandedIdx === 0}
				onChange={(_, expanded) => onChange(expanded, 0)}
				key={0}
				slotProps={{
					heading: {
						component: 'div',
					},
				}}
				data-testid={`management-content`}
			>
				<AccordionSummary>
					<Stack direction="row" width="100%" alignItems="center" gap={1}>
						<Typography variant="h6" component={'span'} fontWeight={'bold'}>
							Maintenance Mode
						</Typography>
						{!noMaintenanceChangesMade && (
							<>
								<Icon sx={{ ml: 'auto' }} color="warning" data-testid="unsaved-changes-banner">
									<Warning />
								</Icon>
								<Typography>Unsaved changes made</Typography>
							</>
						)}
					</Stack>
				</AccordionSummary>
				<AccordionDetails>
					<Stack width="100%" spacing={2} ref={tabHolder}>
						{/* Placeholder description, will probably need changing */}
						<Typography>
							Use this tool to enable/disable Maintenance mode for all or parts of the Prometheus system.
							<br />
							Enabling Maintenance mode will restrict chosen parts of the system to users to enable maintenance to be carried out without
							disruption to the user, preventing unwanted data loss.
						</Typography>
						{error && <Alert severity="error">{error}</Alert>}
						{loading ? (
							<CircularProgress aria-label="Loading list of tabs" />
						) : (
							<>
								<MaintenanceTabGroup
									name={'Whole system'}
									path={'All'}
									disabledTabs={disabledTabs}
									onChangeSelected={onChangeSelected}
									selectedTabs={selectedTabs}
								/>
								{rows.map((_, index) => (
									<Stack direction="row" width="100%" gap={2} key={index}>
										{_.map((tabRow) => (
											<Stack key={tabRow.name} width="33%" gap={0.5}>
												<MaintenanceTabGroup
													name={tabRow.name.concat(' tab group')}
													path={tabRow.name}
													disabledTabs={disabledTabs}
													onChangeSelected={onChangeSelected}
													selectedTabs={selectedTabs}
													childTabs={tabRow.children}
												/>
											</Stack>
										))}
										{Array.from({ length: 3 - _.length }).map((_, index) => (
											<Stack key={index} width="33%" gap={1}></Stack>
										))}
									</Stack>
								))}
							</>
						)}
					</Stack>
				</AccordionDetails>
			</Accordion>
			<Accordion
				expanded={expandedIdx === 1}
				onChange={(_, expanded) => onChange(expanded, 1)}
				key={1}
				slotProps={{
					heading: {
						component: 'div',
					},
				}}
				data-testid={`update-notice-content`}
				sx={{ width: '100%' }}
			>
				<AccordionSummary>
					<Stack direction="row" width="100%" alignItems="center">
						<Typography variant="h6" component={'span'} fontWeight={'bold'}>
							Update Notice Banner
						</Typography>
						{!noNoticeChangesMade && (
							<>
								<Icon sx={{ ml: 'auto' }} color="warning">
									<Warning />
								</Icon>
								<Typography>Unsaved changes made</Typography>
							</>
						)}
					</Stack>
				</AccordionSummary>
				<AccordionDetails>
					<Stack width="100%" spacing={2} ref={tabHolder}>
						{/* Placeholder description, will probably need changing */}
						<Stack direction="row" alignItems="center">
							<Typography>Use this tool to enable/disable the update notice banner on the GUI header.</Typography>
							<Button
								sx={{ ml: 'auto' }}
								variant="outlined"
								onClick={() => {
									setUpdateDescInput('');
									setUpdateTitleInput('');
								}}
							>
								Clear Update Notice
							</Button>
						</Stack>
						{error && <Alert severity="error">{error}</Alert>}
						{loading ? (
							<CircularProgress />
						) : (
							<>
								<TextField
									label="Update Notice Title"
									value={updateTitleInput}
									onChange={(e) => setUpdateTitleInput(e.target.value)}
									data-testid="update-notice-title-input"
								/>
								<TextField
									label="Update Notice Description"
									multiline
									rows={4}
									value={updateDescInput}
									onChange={(e) => setUpdateDescInput(e.target.value)}
								/>
							</>
						)}
					</Stack>
				</AccordionDetails>
			</Accordion>
			<Stack direction="row" width="100%" gap={1}>
				<Button
					variant="contained"
					fullWidth
					endIcon={<CheckCircleOutline />}
					disabled={noMaintenanceChangesMade && noNoticeChangesMade}
					onClick={handleConfirm}
					data-testid="management-confirm-button"
				>
					{loading ? 'Saving changes...' : 'Confirm Changes'}
				</Button>
				<Button
					variant="outlined"
					fullWidth
					endIcon={<RestartAltOutlined />}
					disabled={noMaintenanceChangesMade && noNoticeChangesMade}
					onClick={() => {
						setSelectedTabs(disabledTabs);
						setUpdateDescInput(updateNoticeDesc.join());
						setUpdateTitleInput(updateNoticeTitle);
					}}
					data-testid="management-cancel-button"
				>
					Reset
				</Button>
			</Stack>
			<SnackbarAlert severity="success" open={success} onClose={() => setSuccess(false)} alertMessage="Settings saved successfully" />
		</>
	);
};

export default MaintenanceTab;
